/**
 * WEB-2351 (вариант «б» владельца 08.10) — проверка числа по границам,
 * ЗАДАННЫМ автором. Возврат 1: файл живёт в едином ленивом куске плеера
 * (`player/playerKit.ts`), не в первом экране — его вес держит храповик
 * (`publicEntry.build.web1988`). Шлагбаум экрана (`requiredGate.ts`) зовёт
 * кусок синхронно через `playerKitDoor`; кусок ждёт страница, когда в теле
 * есть блок числа, веса или роста (`numberBoundsDoor.ts`).
 *
 * Граница задана, когда отличается от умолчания блока (`blockDefaults.ts`):
 * число 0–100, вес 30–250, рост 100–230 — «не задано». Имена полей —
 * редактора и генерации (`min`/`max`, как читает `UnitPickerRenderer.tsx`).
 * Вес и рост — только в единице блока по умолчанию (перевод единиц — WEB-2350).
 */
import type { EditorBlock } from '@/store/editorSlice';
import {
  pickerUnitValueKey,
  resolvePickerUnitDefault,
} from './answerCollection';
import { getNumberBoundsStrings } from '@/utils/numberBoundsStrings';

/** Границы числа, заданные автором (незаданной стороны нет). */
export interface AuthorBounds {
  min?: number;
  max?: number;
}

const BOUND_FIELDS: Record<string, readonly [string, string, number, number]> =
  {
    number_input: ['numberMin', 'numberMax', 0, 100],
    weight_picker: ['weightPickerMin', 'weightPickerMax', 30, 250],
    height_picker: ['heightPickerMin', 'heightPickerMax', 100, 230],
  };

function boundOf(raw: unknown, byDefault: number): number | undefined {
  return typeof raw === 'number' && Number.isFinite(raw) && raw !== byDefault
    ? raw
    : undefined;
}

/** Нарушенные границы или `null`; пусто и не-число — не повод. */
export function outOfRange(
  block: EditorBlock,
  values: Record<string, string>,
): AuthorBounds | null {
  const fields = BOUND_FIELDS[block.type];
  const raw = values[block.id];
  if (!fields || typeof raw !== 'string' || raw.trim() === '') return null;
  if (block.type !== 'number_input') {
    const unit = values[pickerUnitValueKey(block.id)];
    if (unit !== undefined && unit !== resolvePickerUnitDefault(block))
      return null;
  }
  const b = block as unknown as Record<string, unknown>;
  const min = boundOf(b[fields[0]] ?? b.min, fields[2]);
  const max = boundOf(b[fields[1]] ?? b.max, fields[3]);
  const n = Number(raw.trim());
  if (!Number.isFinite(n)) return null;
  if ((min === undefined || n >= min) && (max === undefined || n <= max))
    return null;
  return {
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
  };
}

/** Границы для подписи отказа: «18–99», «≥ 18» или «≤ 99». */
export function boundsRange({ min, max }: AuthorBounds): string {
  if (min !== undefined && max !== undefined) return `${min}–${max}`;
  return min !== undefined ? `≥ ${min}` : `≤ ${max}`;
}

/** Подпись отказа под блоком на языке воронки: «Допустимые значения: 18–99». */
export function boundsWarning(
  bounds: AuthorBounds | undefined,
  language?: string | null,
): string {
  return getNumberBoundsStrings(language).numberOutOfRangeWarning.replace(
    '{range}',
    boundsRange(bounds ?? {}),
  );
}
